import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import VERSE_DATA from '../data/verseData';

function VerseGenerator() {
  const [cat, setCat] = useState('salvation');
  const [verse, setVerse] = useState(VERSE_DATA.salvation[0]);
  const [fading, setFading] = useState(false);

  const newVerse = useCallback((category) => {
    const c = category || cat;
    const pool = VERSE_DATA[c];
    const v = pool[Math.floor(Math.random() * pool.length)];
    setFading(true);
    setTimeout(() => { setVerse(v); setFading(false); }, 300);
  }, [cat]);

  const changeCat = (c) => { setCat(c); newVerse(c); };

  return (
    <section className="verse-banner">
      <div className="v-display">
        <div className="oq">"</div>
        <p className="v-text" style={{ opacity: fading ? 0 : 1 }}>{verse[0]}</p>
        <p className="v-ref" style={{ opacity: fading ? 0 : 1 }}>{verse[1]}</p>
        <div className="v-controls">
          {['salvation', 'nation', 'revival', 'courage'].map(c => (
            <button key={c} className={`v-btn${cat === c ? ' active' : ''}`}
              onClick={() => changeCat(c)}>
              {c === 'nation' ? 'For Our Nation' : c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
          <button className="v-btn active" onClick={() => newVerse()}>✝ New Verse</button>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero-flag">
          {Array.from({ length: 13 }, (_, i) => (
            <div key={i} className={`stripe ${i % 2 === 0 ? 'stripe-red' : 'stripe-white'}`} />
          ))}
        </div>
        <div className="hero-cross">✝</div>
        <h1>America Needs<span className="hl">Jesus Christ</span></h1>
        <p className="hero-verse">
          "For I am not ashamed of the gospel of Christ: for it is the power of God unto salvation to every one that believeth; to the Jew first, and also to the Greek."
          <cite>Romans 1:16</cite>
        </p>
        <Link to="/bible/43/3" className="btn-p hero-cta">Open the King James Bible ↓</Link>
        <div className="scroll-ind">▼</div>
      </section>

      <div className="divider" />

      {/* MISSION */}
      <section style={{ background: 'linear-gradient(180deg, var(--navy), var(--deep-blue))' }}>
        <div className="inner">
          <div className="section-label">About KJVUSA</div>
          <h2 className="section-title">Standing on the Foundations<br />That Built This Nation</h2>
          <p style={{ maxWidth: 750, fontSize: '1rem', lineHeight: 1.9, color: 'rgba(245,240,232,.8)', marginBottom: '3rem' }}>
            KJVUSA.org exists for one purpose: to point America to the Lord Jesus Christ. We believe the answer to the issues in America today is not political, but spiritual. The answer is, and always shall be, that we need the Lord Jesus Christ.
          </p>
          <div className="mission-grid">
            <div className="m-card">
              <h3>The Problem</h3>
              <p>America has forsaken the one true God. As the preaching of God's Word has been replaced with feel-good sermons and cultural Christianity, our nation has descended into wickedness. The Bible warns: "The wicked shall be turned into hell, and all the nations that forget God." (Psalm 9:17)</p>
            </div>
            <div className="m-card">
              <h3>The Answer</h3>
              <p>What we need is Jesus Christ. What we need are men and women who have truly been born again through faith in Jesus Christ. The hope for this nation is not found in political power, cultural reform, or intellectual brilliance. It is found in the transforming power of the gospel. We may have capable leaders, gifted philosophers, and influential presidents, but none of them possess the words of eternal life. Only Jesus Christ does. As Peter confessed in John 6:68, <em><b>“Lord, to whom shall we go? thou hast the words of eternal life.”</b></em></p>
            </div>
          </div>
        </div>
      </section>

      <div className="divider divider-gold" />

      {/* YOU NEED JESUS */}
      <section className="you-section">
        <div className="you-inner">
          <div className="you-cross">✝</div>
          <h2>Before We Talk About America,<br />We Need to Talk About You.</h2>
          <p>
            This website is about America's need for God. Yet before we go any further, there is something more important than any nation, any election, or any culture war: the eternal destination of your soul.
          </p>
          <p>
            America is just a country. Countries rise and fall. Empires come and go. Not one of them has an eternal destination. <strong>You do.</strong>
          </p>
          <p className="you-emphasis">
            "For what shall it profit a man, if he shall gain the whole world, and lose his own soul?"<br />
            <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '.85rem', letterSpacing: 3, fontStyle: 'normal' }}>Mark 8:36</span>
          </p>
          <p>
            Every human being will live forever, either in the presence of God or separated from Him in hell. That is not an opinion. That is what the Bible says. Nothing that happens to this nation matters if you die without Jesus Christ as your Saviour.
          </p>
          <p>
            America may or may not turn back to God. <em>You</em> can. Right now. Today.
          </p>
          <div style={{ marginTop: '2.5rem' }}>
            <Link to="/salvation" className="btn-p" style={{ background: 'white', color: 'var(--crimson)', borderColor: 'white' }}>
              How to Be Saved →
            </Link>
          </div>
        </div>
      </section>

      <div className="divider" />


      {/* FOUNDERS */}
      <section style={{ background: 'var(--navy)' }}>
        <div className="inner">
          <div className="section-label">Our Founding Heritage</div>
          <h2 className="section-title">What the Founders Said<br />About the Bible & Christianity</h2>
          <div className="quotes-grid">
            <div className="q-card"><p>It is impossible to rightly govern the world without God and the Bible.</p><cite>George Washington</cite></div>
            <div className="q-card"><p>The Bible is the best of all books, for it is the word of God and teaches us the way to be happy in this world and in the next.</p><cite>John Jay, First Chief Justice</cite></div>
            <div className="q-card"><p>Our Constitution was made only for a moral and religious people. It is wholly inadequate to the government of any other.</p><cite>John Adams</cite></div>
            <div className="q-card"><p>The Bible is the rock on which this Republic rests.</p><cite>Andrew Jackson</cite></div>
            <div className="q-card"><p>I have always said, I always will say, that the studious perusal of the sacred volume will make better citizens, better fathers, and better husbands.</p><cite>Thomas Jefferson</cite></div>
            <div className="q-card"><p>The general principles on which the fathers achieved independence were the general principles of Christianity.</p><cite>John Adams</cite></div>
          </div>
        </div>
      </section>

      <div className="divider" />

      {/* CTA */}
      <section className="cta-section" style={{ background: 'linear-gradient(180deg, var(--deep-blue), var(--navy))' }}>
        <div className="section-label center">Respond Today</div>
        <h2 className="section-title" style={{ textAlign: 'center' }}>What This Country Needs<br />Is the Gospel.</h2>
        <p style={{ maxWidth: 600, margin: '0 auto 2.5rem', fontSize: '1rem', lineHeight: 1.9, color: 'rgba(245,240,232,.8)', textAlign: 'center' }}>
          If you have never trusted Jesus Christ as your personal Saviour, the Bible says: "For whosoever shall call upon the name of the Lord shall be saved." (Romans 10:13). Salvation is not by works, only by grace through faith in the finished work of Jesus Christ on the cross.
        </p>
        <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/salvation" className="btn-p">How to Be Saved</Link>
          <Link to="/bible/43/3" className="btn-s">Read the Gospel of John</Link>
        </div>
      </section>
    </>
  );
}
